import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import * as React from 'react';
import { useRecordContext } from 'react-admin';
import styled from 'styled-components';
import {
  AdminCoachSessionFragment,
  AdminCoachSessionsConnectionFragment,
  AdminGetUserCoachingSessionsDocument,
  AdminGetUserMemberSessionsDocument,
  AdminUserDetailsFragment,
  CoachSessionAttendance,
  CoachSessionType,
} from '../../../../graphql/types';
import { dateAndTime } from '../../../../utils/date';
import { getGraphQLErrorDetails } from '../../../../utils/getGraphQLErrorDetails';
import { getMeetingPlatformLabel } from '../../../../utils/meetingPlatform';
import { ResourceLink } from '../../../reusable/ResourceLink';

const SESSIONS_PAGE_SIZE = 25;

const coachingSessionLabel = 'Coaching Session';
const nutritionSessionLabel = 'Nutrition Session';
const ptSessionLabel = 'Personal Training Session';

const coachSessionLabelMap: Record<CoachSessionType, string> = {
  [CoachSessionType.UserCoachingConsult]: coachingSessionLabel,
  [CoachSessionType.CoachingSession]: coachingSessionLabel,
  [CoachSessionType.UserNutritionConsult]: nutritionSessionLabel,
  [CoachSessionType.PersonalTraining]: ptSessionLabel,
};

const attendanceLabelMap: Record<CoachSessionAttendance, string> = {
  [CoachSessionAttendance.Attended]: 'Attended',
  [CoachSessionAttendance.NoShow]: 'No Show',
  [CoachSessionAttendance.Excused]: 'Excused Absence',
};

const ProfileLabel = styled(Typography)`
  font-weight: bold;
`;

// `cursors[n]` is the `after` cursor for page n; the backend only pages forward
const useCursorPages = () => {
  const [cursors, setCursors] = React.useState<(string | undefined)[]>([
    undefined,
  ]);
  const page = cursors.length - 1;

  const goToPage = (newPage: number, endCursor: string | undefined) => {
    if (newPage > page && endCursor) {
      setCursors((prev) => [...prev, endCursor]);
    } else if (newPage < page) {
      setCursors((prev) => prev.slice(0, newPage + 1));
    }
  };

  return {
    page,
    goToPage,
    input: { first: SESSIONS_PAGE_SIZE, after: cursors[page] },
  };
};

const SessionUserLink: React.FC<{
  user:
    | AdminCoachSessionFragment['coach']
    | AdminCoachSessionFragment['member'];
}> = ({ user }) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return (
    <ResourceLink name="User" id={user.id} type="show">
      {name || user.id}
    </ResourceLink>
  );
};

interface SessionsTableProps {
  title: string;
  counterpart: 'coach' | 'member';
  sessions: AdminCoachSessionsConnectionFragment | null | undefined;
  loading: boolean;
  error: ErrorLike | undefined;
  page: number;
  onPageChange: (newPage: number, endCursor: string | undefined) => void;
}

const SessionsTable: React.FC<SessionsTableProps> = ({
  title,
  counterpart,
  sessions,
  loading,
  error,
  page,
  onPageChange,
}) => {
  const { errorRequestId, errorPath } = getGraphQLErrorDetails(error);

  return (
    <Box mb={4}>
      <ProfileLabel variant="h5">
        {title}
        {sessions ? ` (${sessions.count} total)` : ''}
      </ProfileLabel>
      {loading && !sessions && <Typography>Loading sessions…</Typography>}
      {error && (
        <>
          <Typography color="error">
            An error occurred while fetching sessions.
          </Typography>

          {errorPath && (
            <Typography color="error" fontSize={12}>
              Path: {errorPath}
            </Typography>
          )}
          {errorRequestId && (
            <Typography color="error" fontSize={12}>
              Request Id: {errorRequestId}
            </Typography>
          )}
          {page > 0 && (
            <Button
              size="small"
              onClick={() => onPageChange(0, undefined)}
              sx={{ mt: 1 }}
            >
              Back to first page
            </Button>
          )}
        </>
      )}
      {sessions?.count === 0 && <Typography>None</Typography>}
      {sessions && sessions.count > 0 && (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Style</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>
                  {counterpart === 'coach' ? 'Coach' : 'Member'}
                </TableCell>
                <TableCell>Meeting</TableCell>
                <TableCell>Attendance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.nodes.map((session) => (
                <TableRow key={session.id}>
                  <TableCell>
                    {session.calendar?.start
                      ? dateAndTime(session.calendar.start)
                      : 'Unknown'}
                  </TableCell>
                  <TableCell>{session.sessionStyle}</TableCell>
                  <TableCell>
                    {session.sessionType
                      ? coachSessionLabelMap[session.sessionType]
                      : 'Unknown'}
                  </TableCell>
                  <TableCell>
                    <SessionUserLink
                      user={
                        counterpart === 'coach' ? session.coach : session.member
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {session.meetingUrl ? (
                      <Link
                        href={session.meetingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {getMeetingPlatformLabel(session.meetingUrl)}
                      </Link>
                    ) : (
                      'None'
                    )}
                  </TableCell>
                  <TableCell>
                    {session.attendance
                      ? attendanceLabelMap[session.attendance]
                      : 'Not Tracked'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {sessions.count > SESSIONS_PAGE_SIZE && (
            <TablePagination
              component="div"
              count={sessions.count}
              page={page}
              rowsPerPage={SESSIONS_PAGE_SIZE}
              rowsPerPageOptions={[]}
              onPageChange={(_, newPage) =>
                onPageChange(newPage, sessions.pageInfo.endCursor)
              }
              slotProps={{
                actions: {
                  nextButton: {
                    disabled: loading || !sessions.pageInfo.hasNextPage,
                  },
                  previousButton: { disabled: loading || page === 0 },
                },
              }}
            />
          )}
        </>
      )}
    </Box>
  );
};

const MemberSessions: React.FC<{ userId: string; title: string }> = ({
  userId,
  title,
}) => {
  const { page, goToPage, input } = useCursorPages();
  const { data, error, loading } = useQuery(
    AdminGetUserMemberSessionsDocument,
    {
      variables: { userId, input },
    },
  );

  return (
    <SessionsTable
      title={title}
      counterpart="coach"
      sessions={data?.user?.sessions}
      loading={loading}
      error={error}
      page={page}
      onPageChange={goToPage}
    />
  );
};

const CoachingSessions: React.FC<{ userId: string; title: string }> = ({
  userId,
  title,
}) => {
  const { page, goToPage, input } = useCursorPages();
  const { data, error, loading } = useQuery(
    AdminGetUserCoachingSessionsDocument,
    {
      variables: { userId, input },
    },
  );

  return (
    <SessionsTable
      title={title}
      counterpart="member"
      sessions={data?.user?.coachData?.sessions}
      loading={loading}
      error={error}
      page={page}
      onPageChange={goToPage}
    />
  );
};

export const MemberSessionsTab: React.FC = () => {
  const record = useRecordContext<AdminUserDetailsFragment>();
  if (!record?.id) {
    return null;
  }

  return (
    <Box key={record.id}>
      {record.isCoach && (
        <CoachingSessions userId={record.id} title="Sessions they coach" />
      )}
      <MemberSessions
        userId={record.id}
        title={record.isCoach ? 'Sessions with their own coach' : 'Sessions'}
      />
    </Box>
  );
};
